/**
 * Persistent in-memory cache for list_staples data.
 * Keyed by (userId, listId). Only refetch when cache is empty or invalidated.
 */

export interface ListStaple {
  id: string;
  list_id: string;
  user_id: string;
  item_name: string;
  category: string;
  is_custom: boolean;
  times_purchased: number;
  created_at: string;
}

interface ListStaplesCacheEntry {
  data: ListStaple[];
  key: string;
  timestamp: number;
}

const listStaplesByKey: Record<string, ListStaplesCacheEntry> = {};
let cacheInvalidatedAt = 0;

function cacheKey(userId: string | null, listId: string | null): string | null {
  if (!userId || !listId) return null;
  return `${userId}:${listId}`;
}

export function getCachedListStaples(
  userId: string | null,
  listId: string | null
): ListStaple[] | null {
  const key = cacheKey(userId, listId);
  if (!key) return null;
  const cached = listStaplesByKey[key];
  if (!cached) return null;
  if (cached.timestamp < cacheInvalidatedAt) return null;
  return cached.data;
}

export function setCachedListStaples(
  userId: string,
  listId: string,
  data: ListStaple[]
): void {
  const key = cacheKey(userId, listId);
  if (!key) return;
  listStaplesByKey[key] = { data, key, timestamp: Date.now() };
}

export function invalidateListStaplesCache(): void {
  cacheInvalidatedAt = Date.now();
  Object.keys(listStaplesByKey).forEach((k) => delete listStaplesByKey[k]);
}

export function invalidateListStaplesForList(userId: string, listId: string): void {
  const key = cacheKey(userId, listId);
  if (key) delete listStaplesByKey[key];
}
